import { useApp } from '@/contexts';

import React, { useState } from 'react';

import { DiffEditor } from '@monaco-editor/react';
import {
  Close as CloseIcon,
  ChevronRight as CollapseIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import type { TransformerVersion } from '@/types';

import { useGetRecipeVersionQuery } from '../api/recipeEditorApi';

const COLLAPSED_WIDTH = 36;

interface CompareViewProps {
  onClose: () => void;
  versions: TransformerVersion[];
  currentVersion: number;
}

/**
 * Split diff viewer for comparing recipe versions.
 * Supports collapsing to a thin vertical strip for better usability.
 */
export const CompareView: React.FC<CompareViewProps> = ({ onClose, versions, currentVersion }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { appId } = useApp();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [versionA, setVersionA] = useState(currentVersion);
  const [versionB, setVersionB] = useState(
    versions.length > 1 ? versions[1].version : currentVersion
  );

  const { data: recipeA, isLoading: loadingA } = useGetRecipeVersionQuery({
    baseId: versions[0]?.baseId || '',
    appId: appId || '',
    version: versionA,
  });

  const { data: recipeB, isLoading: loadingB } = useGetRecipeVersionQuery({
    baseId: versions[0]?.baseId || '',
    appId: appId || '',
    version: versionB,
  });

  if (isCollapsed) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: COLLAPSED_WIDTH,
          minWidth: COLLAPSED_WIDTH,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderLeft: 2,
          borderColor: 'divider',
          transition: 'width 0.2s ease',
          py: 1,
          gap: 1,
        }}
      >
        <Tooltip title="Expand Compare" placement="left">
          <IconButton size="small" onClick={() => setIsCollapsed(false)}>
            <CompareArrowsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Close Compare" placement="left">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        width: '50%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 2,
        borderColor: 'divider',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {/* Header */}
      <Toolbar variant="dense" sx={{ bgcolor: 'background.paper', minHeight: 48 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Compare Versions
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 2, mx: 2 }}>
          <Select
            size="small"
            value={versionA}
            onChange={(e) => setVersionA(Number(e.target.value))}
          >
            {versions.map((v) => (
              <MenuItem key={v.id} value={v.version}>
                Version {v.version}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            ↔
          </Typography>
          <Select
            size="small"
            value={versionB}
            onChange={(e) => setVersionB(Number(e.target.value))}
          >
            {versions.map((v) => (
              <MenuItem key={v.id} value={v.version}>
                Version {v.version}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Tooltip title="Collapse">
          <IconButton size="small" onClick={() => setIsCollapsed(true)} sx={{ mr: 0.5 }}>
            <CollapseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* Diff Editor */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
        {loadingA || loadingB ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DiffEditor
            height="100%"
            language="yaml"
            original={recipeA?.config || ''}
            modified={recipeB?.config || ''}
            theme={isDarkMode ? 'vs-dark' : 'vs'}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              renderSideBySide: true,
              enableSplitViewResizing: true,
              scrollBeyondLastLine: false,
              renderOverviewRuler: false,
              overviewRulerLanes: 0,
              scrollbar: {
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10,
                useShadows: false,
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};
